import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useReducedMotion } from 'framer-motion';
import { useMemo } from 'react';

import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { cn } from '../../utils/cn';
import {
  KA_IDLE_PLAYS,
  PROFESSOR_KA_LABEL,
  PROFESSOR_KA_LOTTIE_URL,
  professorKaTileMetrics,
} from './ProfessorKaAvatar.art';
import type {
  ProfessorKaAvatarProps,
  ProfessorKaTileProps,
} from './ProfessorKaAvatar.types';
import { kaTileArtSlotClass, kaTileClass } from './ProfessorKaAvatar.variants';

/**
 * `autoResize` is load-bearing, not cosmetic: dotlottie-web sizes its canvas
 * ONCE at init from the container's then-current box and only attaches a
 * ResizeObserver when autoResize is set. Ka mounts in containers whose size
 * settles late — the docked pane's frozen-width transitions, the inert Create
 * slide, the floating panel's entrance, the avatar's layoutId glide (scale
 * transforms skew the measured rect mid-flight) — so without it the canvas
 * rasterizes at a degenerate size and the box stretches the stale bitmap into
 * a squashed Ka. Module constant so the react wrapper never sees a fresh
 * config object on chat's frequent re-renders.
 */
const KA_RENDER_CONFIG = { autoResize: true } as const;

/**
 * Professor Ka's animated avatar — WEB.
 *
 * Same contract as `ProfessorKaAvatar.native.tsx`; the ONLY fork is the Lottie
 * renderer. Web STREAMS the hosted `.lottie` while native bundles the parsed
 * composition — see `ProfessorKaAvatar.art`, which keeps the two named side by
 * side so they cannot be revved apart.
 *
 * `size` is in DS points and `useResponsiveScale()` is 1 here, because CSS rem
 * already ramps the surrounding layout on web. The multiply stays so both
 * platforms run the identical expression.
 */
export function ProfessorKaAvatar({
  size,
  unscaled = false,
  className,
  pixelRatio,
  testID,
}: ProfessorKaAvatarProps) {
  // An unconditional loop ignores the OS "reduce motion" setting, and this
  // avatar is mounted persistently in chat — so it animated forever for users
  // who asked the system not to.
  const reduceMotion = useReducedMotion();
  const scale = useResponsiveScale();
  const edge = size == null ? undefined : unscaled ? size : size * scale;
  // Memoised for the same reason KA_RENDER_CONFIG is a module constant: a
  // fresh config object per render would re-init the player.
  const renderConfig = useMemo(
    () =>
      pixelRatio == null
        ? KA_RENDER_CONFIG
        : { ...KA_RENDER_CONFIG, devicePixelRatio: pixelRatio },
    [pixelRatio],
  );
  return (
    <span
      role="img"
      data-testid={testID}
      aria-label={PROFESSOR_KA_LABEL}
      style={edge == null ? undefined : { width: edge, height: edge }}
      className={cn('inline-flex flex-none items-center justify-center', className)}>
      <DotLottieReact
        src={PROFESSOR_KA_LOTTIE_URL}
        loop={!reduceMotion}
        // A few plays, then a still picture — see KA_IDLE_PLAYS (QA #35).
        loopCount={KA_IDLE_PLAYS - 1}
        autoplay={!reduceMotion}
        renderConfig={renderConfig}
        className="h-full w-full"
      />
    </span>
  );
}

/**
 * Ka's tile — WEB. A `<button>` with `onPress`, a static `<div>` without.
 *
 * Geometry comes from `professorKaTileMetrics` exactly as on native — the
 * calibrated border-box framing (see `KA_TILE_ART_RATIO`), the art seated
 * absolutely at `artOffset` (−border) — so the two clients cannot drift into
 * two differently-proportioned tiles again.
 */
export function ProfessorKaTile({
  onPress,
  accessibilityLabel,
  size,
  className,
  renderArt,
  testID,
}: ProfessorKaTileProps) {
  const scale = useResponsiveScale();
  const ownCard = renderArt != null;
  const { m, frameStyle, artStyle } = useMemo(() => {
    const m = professorKaTileMetrics(scale, size);
    return {
      m,
      frameStyle: {
        width: m.size,
        height: m.size,
        borderRadius: m.radius,
        // Injected art draws its own ring (see `renderArt`).
        borderWidth: ownCard ? 0 : m.border,
        borderStyle: 'solid' as const,
      },
      artStyle: { top: m.artOffset, left: m.artOffset },
    };
  }, [scale, size, ownCard]);
  const frameClass = cn(
    ownCard ? kaTileArtSlotClass : kaTileClass,
    'relative flex',
    className,
  );
  const artwork = ownCard ? (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      {renderArt(m)}
    </span>
  ) : (
    <span aria-hidden className="pointer-events-none absolute" style={artStyle}>
      <ProfessorKaAvatar size={m.art} unscaled />
    </span>
  );

  if (!onPress) {
    return (
      <div className={frameClass} style={frameStyle} data-testid={testID}>
        {artwork}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onPress}
      data-testid={testID}
      aria-label={accessibilityLabel}
      style={{ ...frameStyle, boxShadow: '0px 14px 21px rgba(0, 0, 0, 0.1)' }}
      className={frameClass}>
      {artwork}
    </button>
  );
}
